import { MILLISECONDS_IN_MINUTE } from '@stdlib/constants-time';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';

import { CORE_QUERY_KEYS as HOTEL_PACKS_CORE_QUERY_KEYS } from 'app/hooks/data/core/extranet/hotel';
import { CORE_QUERY_KEYS as INVENTORY_CORE_QUERY_KEYS } from 'app/hooks/data/core/extranet/inventory';
import { hotelPath } from 'app/hooks/data/hotels/useHotel';
import type { DayOfWeek } from 'app/typings/core/admin/enum/dayOfWeek';
import { apiMutation, apiRequest } from 'app/utils/request/api';

import { CORE_QUERY_KEYS, ENDPOINTS } from '.';

type AverageRoomPrice = {
  average: number | null;
  count: number | null;
  days?: Partial<Record<DayOfWeek, number>>;
};

export function useDiscountAllocation(
  hotelId: number,
  isPricingDrawerOpened: boolean
) {
  return useQuery({
    queryKey: [CORE_QUERY_KEYS.discountAllocation, { hotelId }],
    queryFn: () =>
      apiRequest({
        path: ENDPOINTS.getDiscountAllocation,
        params: { id: hotelId.toString() },
      }) as Promise<AverageRoomPrice>,
    staleTime: 5 * MILLISECONDS_IN_MINUTE,
    enabled: isPricingDrawerOpened,
  });
}

type ExperienceDiscount = {
  experienceId: number;
  discount: number;
};

type DefaultNightRoomDiscount =
  | { type: 'CONSTANT'; rate: number }
  | { type: 'DAILY'; days: Record<DayOfWeek, number> };

type PayloadProps = {
  hotelId: number;
  averageRoomPrice: number;
  defaultNightRoomDiscount: DefaultNightRoomDiscount;
  lastMinuteNightRoomDiscountRate?: number | null;
  lastMinuteNightRoomDiscountDays?: number | null;
  packs: { packId: number; experienceDiscounts: ExperienceDiscount[] }[];
  overrideManualData: boolean;
};

export function useSaveDiscountAllocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: PayloadProps) =>
      apiMutation(
        'POST',
        {
          path: ENDPOINTS.saveDiscountAllocation,
          params: {
            id: payload.hotelId.toString(),
          },
        },
        payload
      ) as Promise<void>,
    onSuccess: (_, payload) => {
      queryClient.invalidateQueries({
        queryKey: [
          CORE_QUERY_KEYS.discountAllocation,
          { hotelId: payload.hotelId },
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [INVENTORY_CORE_QUERY_KEYS.inventory],
      });
      queryClient.invalidateQueries({
        queryKey: [hotelPath, { hotelId: payload.hotelId }],
      });
      queryClient.invalidateQueries({
        queryKey: [
          HOTEL_PACKS_CORE_QUERY_KEYS.hotelPacks,
          { id: payload.hotelId },
        ],
      });

      queryClient.invalidateQueries({
        queryKey: [
          INVENTORY_CORE_QUERY_KEYS.packScore,
          { hotelId: payload.hotelId },
        ],
      });

      message.success('Discount allocation saved successfully');
    },
    onError: () => {
      message.error('Error while saving discount allocation');
    },
  });
}
